import { styled, useTranslation, Text, useMutation, css } from '@obvio/app'
import {
  Form,
  Stack,
  TextInput,
  CheckboxInput,
  Submit,
  Grid,
  GridItem,
} from '@obvio/ui'
import { useCallback } from 'react'

import { EventHeading } from './EventHeading'
import { RegisterMoreButton } from './RegisterMoreButton'
import { ModalInputs } from '../Modal'
import { useLocalStorageEvent } from '@/utils/hooks/useLocalStorageEvent'

import type { AllowNullishForOptional } from '@obvio/utils'
import type { ReactElement } from 'react'

const TextStack = styled(Stack)<{ $modal: boolean }>`
  margin-left: auto;
  margin-right: auto;
  ${(_, { $modal }) =>
    $modal &&
    css`
      margin: 0;
    `}
`

const CTA = styled(Submit)<{ $modal: boolean }>`
  width: fit-content;
  margin: 0 auto !important;
  margin-top: ${(theme, { $modal }) =>
    !$modal && `calc(${theme.spacing.large} * 2)`} !important;
`

const ContentStack = styled(Stack)`
  width: 100%;
  max-width: 68.25rem;
  margin: 0 auto;
  color: white;
`

const ButtonGridItem = styled(GridItem)<{ $modal: boolean }>`
  ${(_, { $modal }) =>
    $modal &&
    css`
      display: flex;
      justify-content: flex-end;
      > button {
        margin: 0 !important;
      }
    `}
`

export type EventRegisterFormProps = AllowNullishForOptional<{
  title: string
  date: string
  place?: string
  placeUrl?: string
  rodoRequired: boolean
  id: string
  hasPlaceLeft: boolean
  modal?: boolean
  onNewClick?(): void
  accept(): void
}>

type GuestFormData = {
  email: string
  name: string
  phone: string
}

export function EventRegisterForm({
  title,
  date,
  place,
  placeUrl,
  rodoRequired,
  id,
  modal = false,
  accept,
}: EventRegisterFormProps): ReactElement {
  const { t } = useTranslation()
  const [registerGuest] = useMutation('registerGuest')
  const { registeredEvents, registerEvent, removeEvent } =
    useLocalStorageEvent()

  const submit = useCallback(
    async ({ email, name, phone }: GuestFormData) => {
      await registerGuest({
        email,
        name,
        phone,
        eventId: id,
      })

      registerEvent(id)
      accept()
    },
    [registerGuest, id, registerEvent, accept],
  )

  const registered = registeredEvents?.some((eventId) => id === eventId)
  return (
    <Form contextOnly onSubmit={submit}>
      <ContentStack kind="vertical" spacing="large" fullWidth>
        <TextStack $modal={Boolean(modal)} kind="vertical" spacing="extraSmall">
          {registered ? (
            <>
              <Text>{t('event.registered')}</Text>
              <Text>{t('event.approveMessage')}</Text>
            </>
          ) : (
            <EventHeading
              title={title}
              date={date}
              place={place ?? undefined}
              placeUrl={placeUrl ?? undefined}
              small
            />
          )}
        </TextStack>
        {registered ? (
          <RegisterMoreButton id={id} onNewClick={() => removeEvent(id)} />
        ) : (
          <>
            <ModalInputs kind="vertical" spacing="medium">
              <TextInput name="name" label={t('form.label.fullName')} />
              <TextInput name="email" label={t('form.label.email')} />
              <TextInput name="phone" label={t('form.label.number')} />
            </ModalInputs>
            <Grid {...(modal && { templateColumns: '1fr 1fr' })} gap="medium">
              <GridItem>
                {rodoRequired ? (
                  <CheckboxInput name="rodo" label={t('form.rodo')} required />
                ) : null}
              </GridItem>
              <ButtonGridItem noCenter $modal={Boolean(modal)}>
                <CTA $modal={Boolean(modal)} {...(!modal && { kind: 'ghost' })}>
                  {t('event.saveOnList')}
                </CTA>
              </ButtonGridItem>
            </Grid>
          </>
        )}
      </ContentStack>
    </Form>
  )
}
